import './StoreForm.scss';

import type { StorePermission, StoreV2 } from 'api/models/unified';
import { Button, FlexRow, LoadingIndicator, ModalFooter } from 'components';
import { FormWrapper, SubmitButton, TextInput } from 'components/forms/hook-form';
import { useCustomTranslation } from 'helpers/translationHelper';
import { useUsers } from 'helpers/usersHelper';
import React from 'react';
import type { SubmitHandler } from 'react-hook-form';

import StoreFormAvailableForHandler from './StoreFormAvailableForHandler';

interface StoreFormV2Props {
  onSubmit: SubmitHandler<StoreFormData>;
  store?: StoreV2;
  onCancel: () => void;
}

export interface StoreFormData {
  name: string;
  externalId?: string | null;
  availableFor: Record<string, StorePermission>;
}

export interface StoreFormValues {
  name: string;
  availableFor: Record<string, { enabled: boolean; permission: StorePermission }>;
  externalId?: string | null;
}

const StoreFormV2 = ({ onSubmit, onCancel, store }: StoreFormV2Props) => {
  const { isLoading, isUserExist } = useUsers();

  const { tInput, tMaterial } = useCustomTranslation();

  if (isLoading) return <LoadingIndicator />;

  const defaultValues: StoreFormValues = {
    name: store?.name || '',
    availableFor: store?.availableFor
      ? store.availableFor.reduce((acc, permission) => {
          if (isUserExist(permission.userId)) {
            return {
              ...acc,
              [permission.userId]: { enabled: true, permission: permission.permissionLevel }
            };
          }

          return acc;
        }, {})
      : {},
    externalId: store?.externalId
  };

  const handleSubmit = (data: StoreFormValues) => {
    const formattedData: StoreFormData = {
      name: data.name,
      externalId: data.externalId,
      availableFor: Object.keys(data.availableFor).reduce((acc, userId) => {
        const userPermission = data.availableFor[userId].permission;

        if (data.availableFor[userId].enabled) {
          return { ...acc, [userId]: userPermission };
        }

        return acc;
      }, {})
    };

    onSubmit(formattedData);
  };

  return (
    <FormWrapper<StoreFormValues>
      onSubmit={handleSubmit}
      defaultValues={defaultValues}
      className="StoreForm"
    >
      <TextInput name="name" required label={tMaterial('store_name')} />
      <TextInput name="externalId" label={tInput('external_id')} />
      <StoreFormAvailableForHandler store={store} />
      <ModalFooter>
        <FlexRow noMargin>
          <Button onClick={onCancel} className="mr-1">
            {tInput('cancel_button')}
          </Button>
          <SubmitButton />
        </FlexRow>
      </ModalFooter>
    </FormWrapper>
  );
};

export default StoreFormV2;
